/**
 * Pure decoder for the `final / response.metadata` stream event (UX-02).
 *
 * Extracted verbatim from App.tsx's inline `onSseEvent` handler — a ~150-line,
 * ~60-field object literal that the audit flagged as effectively untestable
 * inside the god component. Behaviour is preserved exactly; the only input
 * beyond the raw payload is `chartBriefingTurn` (which the caller already
 * computes to also clear widgets on those turns).
 *
 * Every field is defensively narrowed — the backend payload is untyped and a
 * wrong shape must degrade to `undefined`, never crash the stream.
 */
import type { ResponseMetadata } from '../../services/threadService';

export interface DecodeMetadataOptions {
  /** chart_briefing turns suppress tools_used and gate the structured payload. */
  chartBriefingTurn: boolean;
}

export function decodeResponseMetadata(
  meta: Record<string, unknown>,
  { chartBriefingTurn }: DecodeMetadataOptions,
): ResponseMetadata {
  const strArray = (v: unknown): string[] | undefined =>
    Array.isArray(v) ? (v as unknown[]).map((x) => String(x)) : undefined;

  return {
    tools_used: chartBriefingTurn
      ? []
      : Array.isArray(meta.tools_used)
        ? (meta.tools_used as string[])
        : undefined,
    confidence_score: typeof meta.confidence_score === 'number' ? meta.confidence_score : undefined,
    processing_time_ms: typeof meta.processing_time_ms === 'number' ? meta.processing_time_ms : undefined,
    intent_type: typeof meta.intent_type === 'string' ? meta.intent_type : undefined,
    execution_flow: typeof meta.execution_flow === 'string' ? meta.execution_flow : undefined,
    disclaimer: typeof meta.disclaimer === 'string' ? meta.disclaimer : undefined,
    // #106: the ADK response-contract label was computed server-side and
    // read by Message.tsx's footer, but never survived this decode step -
    // the footer always fell back to the flow-code label.
    agentic_core_adk_response_contract:
      typeof meta.agentic_core_adk_response_contract === 'string'
        ? meta.agentic_core_adk_response_contract
        : undefined,
    structured:
      meta.structured && typeof meta.structured === 'object'
        ? (() => {
            const structuredPayload = meta.structured as Record<string, unknown>;
            const structuredMeta =
              structuredPayload.meta && typeof structuredPayload.meta === 'object'
                ? (structuredPayload.meta as Record<string, unknown>)
                : {};
            const keepChartBriefingStructured =
              !chartBriefingTurn ||
              structuredMeta.chart_briefing_presentation === true ||
              structuredMeta.summary_narrative_card === true;
            return keepChartBriefingStructured ? structuredPayload : undefined;
          })()
        : undefined,
    code_references: Array.isArray(meta.code_references)
      ? (meta.code_references as Array<{ term: string; code: string; system: string }>)
      : undefined,
    rag_sources: Array.isArray(meta.rag_sources)
      ? (meta.rag_sources as Array<{
          source: string;
          content_preview?: string;
          resource?: string;
          chunk_id?: string;
          document_id?: string;
          score?: string;
        }>)
      : undefined,
    rag_spans: Array.isArray(meta.rag_spans)
      ? (meta.rag_spans as Array<{ start: number; end: number; resource: string }>)
      : undefined,
    // #49 (3x recurrence): the citation-free guideline-evidence contract that
    // drives GuidelineEvidencePanel. Like #106/#177 above, the backend built
    // and emitted this field but it never survived this allow-list decoder, so
    // the panel never rendered regardless of the backend fix. Object-narrow it
    // exactly like structured / router_overview_payload — a wrong shape must
    // degrade to undefined (panel absent), never crash the stream.
    guideline_evidence:
      meta.guideline_evidence && typeof meta.guideline_evidence === 'object' && !Array.isArray(meta.guideline_evidence)
        ? (meta.guideline_evidence as Record<string, unknown>)
        : undefined,
    suppress_clinical_references: meta.suppress_clinical_references === true ? true : undefined,
    router_overview: typeof meta.router_overview === 'string' ? meta.router_overview : undefined,
    router_overview_payload:
      meta.router_overview_payload && typeof meta.router_overview_payload === 'object'
        ? (meta.router_overview_payload as Record<string, unknown>)
        : undefined,
    specialty_output_mode:
      typeof meta.specialty_output_mode === 'string' ? meta.specialty_output_mode : undefined,
    specialty_outputs: Array.isArray(meta.specialty_outputs)
      ? (meta.specialty_outputs as Array<Record<string, unknown>>)
      : undefined,
    specialty_routing:
      typeof meta.specialty_routing === 'string'
        ? meta.specialty_routing
        : meta.specialty_routing && typeof meta.specialty_routing === 'object'
          ? (meta.specialty_routing as Record<string, unknown>)
          : undefined,
    dual_specialty: typeof meta.dual_specialty === 'boolean' ? meta.dual_specialty : undefined,
    engaged_specialties: strArray(meta.engaged_specialties),
    skipped_specialties_without_rag: strArray(meta.skipped_specialties_without_rag),
    suggested_next_questions: strArray(meta.suggested_next_questions),
    // #177: authoritative Section-1 text for live-streamed suppress routes —
    // must survive the decode whitelist or the client swap never happens.
    authoritative_final_text:
      typeof meta.authoritative_final_text === 'string' && meta.authoritative_final_text.trim()
        ? meta.authoritative_final_text
        : undefined,
    answer_contract_passed:
      typeof meta.answer_contract_passed === 'boolean' ? meta.answer_contract_passed : undefined,
    answer_contract_reasons: strArray(meta.answer_contract_reasons),
    high_risk_user_prompt:
      typeof meta.high_risk_user_prompt === 'boolean' ? meta.high_risk_user_prompt : undefined,
    high_risk_hits: strArray(meta.high_risk_hits),
    clinical_thinking_flow: Array.isArray(meta.clinical_thinking_flow)
      ? (meta.clinical_thinking_flow as unknown[])
          .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const o = item as Record<string, unknown>;
            return {
              step: typeof o.step === 'string' ? o.step : String(o.step ?? ''),
              title: typeof o.title === 'string' ? o.title : String(o.title ?? ''),
              body: typeof o.body === 'string' ? o.body : String(o.body ?? ''),
            };
          })
          .filter((x): x is { step: string; title: string; body: string } => x != null)
      : undefined,
    clinical_rubric_scores: (() => {
      const s = meta.clinical_rubric_scores;
      if (!s || typeof s !== 'object' || Array.isArray(s)) return undefined;
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(s as Record<string, unknown>)) {
        const n = typeof v === 'number' ? v : Number(v);
        if (Number.isFinite(n)) out[k] = n;
      }
      return Object.keys(out).length ? out : undefined;
    })(),
    clinical_quality_iterations:
      typeof meta.clinical_quality_iterations === 'number' ? meta.clinical_quality_iterations : undefined,
    clinical_quality_gate_satisfied:
      meta.clinical_quality_gate_satisfied === null
        ? null
        : typeof meta.clinical_quality_gate_satisfied === 'boolean'
          ? meta.clinical_quality_gate_satisfied
          : undefined,
    clinical_quality_refinement_skipped:
      typeof meta.clinical_quality_refinement_skipped === 'string'
        ? meta.clinical_quality_refinement_skipped
        : undefined,
    clinical_quality_refinement_skipped_reason:
      typeof meta.clinical_quality_refinement_skipped_reason === 'string'
        ? meta.clinical_quality_refinement_skipped_reason
        : typeof meta.clinical_quality_refinement_skipped === 'string'
          ? meta.clinical_quality_refinement_skipped
          : undefined,
    clinical_stability_warnings: strArray(meta.clinical_stability_warnings),
    clinical_residual_risk_summary:
      typeof meta.clinical_residual_risk_summary === 'string'
        ? meta.clinical_residual_risk_summary
        : undefined,
    clinical_hard_constraints_met:
      typeof meta.clinical_hard_constraints_met === 'boolean' ? meta.clinical_hard_constraints_met : undefined,
    clinical_soft_targets_met:
      typeof meta.clinical_soft_targets_met === 'boolean' ? meta.clinical_soft_targets_met : undefined,
    clinical_regression_detected: meta.clinical_regression_detected === true ? true : undefined,
    clinical_quality_plateau_detected: meta.clinical_quality_plateau_detected === true ? true : undefined,
    clinical_confidence_score:
      typeof meta.clinical_confidence_score === 'number' && Number.isFinite(meta.clinical_confidence_score)
        ? meta.clinical_confidence_score
        : undefined,
    clinical_quality_layer_authoritative:
      typeof meta.clinical_quality_layer_authoritative === 'boolean'
        ? meta.clinical_quality_layer_authoritative
        : undefined,
    clinical_quality_gate_status:
      typeof meta.clinical_quality_gate_status === 'string' ? meta.clinical_quality_gate_status : undefined,
  };
}
